import { Component, Input, ChangeDetectionStrategy, booleanAttribute } from '@angular/core';

export type SkeletonVariant = 'text' | 'title' | 'avatar' | 'card' | 'table-row' | 'kpi-card' | 'custom';

/**
 * Skeleton loader com animação shimmer.
 * Usa role="status" + aria-label para anunciar estado de carregamento.
 *
 * @usage
 * <!-- Linha de texto -->
 * <ui-skeleton variant="text" />
 *
 * <!-- Card KPI (dashboard) -->
 * <ui-skeleton variant="kpi-card" />
 *
 * <!-- Linha de tabela -->
 * <ui-skeleton variant="table-row" />
 *
 * <!-- Múltiplas linhas -->
 * <ui-skeleton variant="text" [repeat]="3" />
 *
 * <!-- Dimensão customizada -->
 * <ui-skeleton variant="custom" width="120px" height="32px" [rounded]="true" />
 */
@Component({
  selector: 'ui-skeleton',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div role="status" [attr.aria-label]="label">

      @switch (variant) {

        @case ('text') {
          <div class="flex flex-col gap-2">
            @for (_ of rows; track $index) {
              <div
                class="skeleton-shimmer rounded h-4"
                [style.width]="$last ? '60%' : '100%'"
              ></div>
            }
          </div>
        }

        @case ('title') {
          <div class="skeleton-shimmer rounded-md h-6 w-48"></div>
        }

        @case ('avatar') {
          <div class="skeleton-shimmer rounded-full w-10 h-10 shrink-0"></div>
        }

        @case ('card') {
          <div class="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <div class="skeleton-shimmer rounded-md h-5 w-32"></div>
            <div class="skeleton-shimmer rounded h-3 w-full"></div>
            <div class="skeleton-shimmer rounded h-3 w-3/4"></div>
          </div>
        }

        @case ('kpi-card') {
          <div class="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
            <div class="skeleton-shimmer rounded-xl w-12 h-12 shrink-0"></div>
            <div class="flex-1 space-y-2">
              <div class="skeleton-shimmer rounded h-3 w-24"></div>
              <div class="skeleton-shimmer rounded-md h-7 w-32"></div>
              <div class="skeleton-shimmer rounded h-3 w-20"></div>
            </div>
          </div>
        }

        @case ('table-row') {
          <div class="flex items-center gap-4 px-4 py-3 border-b border-gray-100">
            <div class="skeleton-shimmer rounded h-3 w-24"></div>
            <div class="skeleton-shimmer rounded h-3 flex-1"></div>
            <div class="skeleton-shimmer rounded h-3 w-20"></div>
            <div class="skeleton-shimmer rounded h-3 w-16"></div>
            <div class="skeleton-shimmer rounded-full h-5 w-16"></div>
          </div>
        }

        @case ('custom') {
          <div
            class="skeleton-shimmer"
            [style.width]="width"
            [style.height]="height"
            [class.rounded-full]="rounded"
            [class.rounded-lg]="!rounded"
          ></div>
        }

      }

      <span class="sr-only">{{ label }}</span>
    </div>
  `,
})
export class UiSkeletonComponent {
  @Input() variant: SkeletonVariant = 'text';
  @Input() label = 'Carregando…';

  /** Para variant="text": quantas linhas renderizar */
  @Input() repeat = 1;

  /** Para variant="custom" */
  @Input() width  = '100%';
  @Input() height = '16px';
  @Input({ transform: booleanAttribute }) rounded = false;

  get rows(): unknown[] {
    return Array(this.repeat);
  }
}
